import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { fr } from "../content/fr";
import { IMG } from "../lib/images";
import { fetchAngle } from "../lib/api";
import Seo from "../components/Seo";
import { breadcrumbLd } from "../seo/jsonld";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal, LiveBadge } from "../components/ui-bits";

const VERDICT_LABEL = { greenlight: "Validé", test: "En test", retest: "À retester", kill: "Abandonné" };

export default function Angle() {
  const { slug } = useParams();
  const { data: angle, isLoading, isError } = useQuery({ queryKey: ["angle", slug], queryFn: () => fetchAngle(slug), retry: false });

  return (
    <div>
      <Seo
        routeKey="/o"
        path={`/o/${slug}`}
        title={angle ? `${angle.titre} | VIRALYA` : undefined}
        description={angle ? angle.hook : undefined}
        jsonLd={angle ? breadcrumbLd([{ name: "Accueil", path: "/" }, { name: "Secteurs", path: "/#angles" }, { name: angle.secteur, path: `/o/${angle.slug}` }]) : undefined}
      />
      <Header />
      <main className="page">
        <div className="container">
          <Link to="/#angles" className="back-link"><ArrowLeft size={15} /> Tous les secteurs</Link>

          {isLoading && <p className="muted">Chargement…</p>}
          {isError && (
            <div className="glass card center" style={{ padding: "3rem" }} data-testid="angle-notfound">
              <h1 className="h2">Angle introuvable</h1>
              <p className="lead center" style={{ marginTop: "1rem" }}>Ce secteur n'est pas disponible.</p>
              <Link to="/" className="btn btn--primary" style={{ marginTop: "1.5rem" }}>Retour à l'accueil</Link>
            </div>
          )}

          {angle && (
            <>
              <div className="offer-wrap" style={{ marginBottom: "3rem" }} data-testid="angle-hero">
                <Reveal>
                  <div style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem", alignItems: "center" }}>
                    <span className="mono" style={{ color: "var(--brand-violet)" }}>/o/{angle.slug}</span>
                    <span className={`angle-verdict verdict-${angle.verdict}`} data-testid="angle-verdict">{VERDICT_LABEL[angle.verdict] || angle.verdict}</span>
                  </div>
                  <h1 className="h1">{angle.titre}</h1>
                  <p className="lead" style={{ marginTop: "1.2rem" }}>{angle.hook}</p>
                  <Link to="/pilote" className="btn btn--primary" style={{ marginTop: "1.8rem" }} data-testid="angle-cta">
                    {angle.cta || fr.offer.cta} <ArrowRight size={16} />
                  </Link>
                </Reveal>
                <Reveal delay={0.1} style={{ position: "relative", borderRadius: 24, overflow: "hidden", boxShadow: "var(--card-shadow)" }}>
                  <img src={IMG.webinar} alt={angle.secteur} style={{ width: "100%", display: "block" }} />
                  <div style={{ position: "absolute", left: 16, top: 16 }}><LiveBadge label={angle.secteur.toUpperCase()} /></div>
                </Reveal>
              </div>

              <div className="grid-3">
                {(angle.blocks || []).slice(0, 6).map((b, i) => (
                  <Reveal className="glass card" key={i} delay={i * 0.06}>
                    <div className="mono gradient-text" style={{ fontWeight: 700, marginBottom: "0.6rem" }}>{String(i + 1).padStart(2, "0")}</div>
                    <h3 className="card__title">{b.h}</h3>
                    <p className="card__desc">{b.p}</p>
                  </Reveal>
                ))}
              </div>

              <div className="final-banner" style={{ marginTop: "3rem" }}>
                <h2 className="h2">{fr.finalCta.title}</h2>
                <p className="lead" style={{ margin: "1rem auto 1.75rem" }}>{fr.finalCta.body}</p>
                <Link to="/pilote" className="btn btn--light" data-testid="angle-cta-bottom">{fr.offer.cta} <ArrowRight size={16} /></Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
